import React, { useEffect, useRef, useState } from 'react';

export const BlueprintCanvas: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [coords, setCoords] = useState({ x: 0, y: 0, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let mouseX = -100;
    let mouseY = -100;
    let targetMouseX = -100;
    let targetMouseY = -100;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resize();
    window.addEventListener('resize', resize);

    const handleMouseMove = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      targetMouseX = e.clientX - rect.left;
      targetMouseY = e.clientY - rect.top;
      setCoords({
        x: Math.round(targetMouseX * 1.5),
        y: Math.round(targetMouseY * 1.5),
        active: true
      });
    };

    const handleMouseLeave = () => {
      targetMouseX = -100;
      targetMouseY = -100;
      setCoords(prev => ({ ...prev, active: false }));
    };

    window.addEventListener('mousemove', handleMouseMove);
    document.body.addEventListener('mouseleave', handleMouseLeave);

    const draw = () => {
      // Smooth mouse interpolation
      mouseX += (targetMouseX - mouseX) * 0.15;
      mouseY += (targetMouseY - mouseY) * 0.15;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      const gridSize = 48;
      const width = canvas.width;
      const height = canvas.height;

      // Draw subtle background grid dots and crosses
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.025)';
      ctx.lineWidth = 1;

      // Grid lines
      ctx.beginPath();
      for (let x = 0; x <= width; x += gridSize) {
        ctx.moveTo(x, 0);
        ctx.lineTo(x, height);
      }
      for (let y = 0; y <= height; y += gridSize) {
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
      }
      ctx.stroke();

      // Crosshairs at intersections
      const crossSize = 3;
      ctx.strokeStyle = 'rgba(197, 168, 128, 0.15)'; // Accent bronze
      ctx.beginPath();
      for (let x = gridSize * 2; x < width; x += gridSize * 2) {
        for (let y = gridSize * 2; y < height; y += gridSize * 2) {
          ctx.moveTo(x - crossSize, y);
          ctx.lineTo(x + crossSize, y);
          ctx.moveTo(x, y - crossSize);
          ctx.lineTo(x, y + crossSize);
        }
      }
      ctx.stroke();

      // Mouse interactive crosshair and drafting target
      if (mouseX > 0 && mouseY > 0) {
        // Subtle glow circle
        const gradient = ctx.createRadialGradient(mouseX, mouseY, 0, mouseX, mouseY, 140);
        gradient.addColorStop(0, 'rgba(56, 189, 248, 0.08)'); // blueprint cyan
        gradient.addColorStop(1, 'transparent');
        ctx.fillStyle = gradient;
        ctx.fillRect(mouseX - 140, mouseY - 140, 280, 280);

        // Technical crosshair lines
        ctx.strokeStyle = 'rgba(56, 189, 248, 0.25)';
        ctx.lineWidth = 1;
        ctx.setLineDash([4, 4]);

        // Horizontal line
        ctx.beginPath();
        ctx.moveTo(0, mouseY);
        ctx.lineTo(width, mouseY);
        ctx.stroke();

        // Vertical line
        ctx.beginPath();
        ctx.moveTo(mouseX, 0);
        ctx.lineTo(mouseX, height);
        ctx.stroke();
        ctx.setLineDash([]);

        // Central crosshair target
        ctx.strokeStyle = 'rgba(197, 168, 128, 0.7)';
        ctx.beginPath();
        ctx.arc(mouseX, mouseY, 12, 0, Math.PI * 2);
        ctx.stroke();

        ctx.fillStyle = '#C5A880';
        ctx.beginPath();
        ctx.arc(mouseX, mouseY, 2, 0, Math.PI * 2);
        ctx.fill();
      }

      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      document.body.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  return (
    <>
      <canvas
        ref={canvasRef}
        className="fixed inset-0 pointer-events-none z-0 opacity-80 transition-opacity duration-500"
      />
      {coords.active && (
        <div className="fixed bottom-4 right-4 z-40 hidden md:flex items-center gap-3 px-3 py-1.5 rounded bg-[#121418]/80 backdrop-blur border border-white/10 text-[10px] font-mono text-arch-muted tracking-wider pointer-events-none">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-arch-cyan animate-pulse"></span>
          <span>CAD COORD: X:{coords.x}mm Y:{coords.y}mm</span>
          <span className="text-white/20">|</span>
          <span className="text-arch-accent font-medium">SCALE 1:100</span>
        </div>
      )}
    </>
  );
};
